/**
 * WAS WIRKLICH ABGERECHNET WIRD - die reinen Regeln des LEB-Fensters.
 *
 * ORIGINALTEXT MARVIN (08.09.2026, Punkt 14 der offenen Punkte)
 *   „LEB-Export: editierbar machen, dazu die Gegenüberstellung zu dem, was
 *    unser System gerechnet hat"
 *
 * ORIGINALTEXT MARVIN (14.09.2026)
 *   „den vermerk muss ich je position ändern können."
 *
 * DIESE DATEI TRÄGT KEIN JSX UND KEIN NETZ, und das ist der Grund, warum es
 * sie gibt: eine Datei mit JSX lässt sich mit `--experimental-strip-types`
 * nicht laden, und dann bliebe nur die Textsuche - **eine Textsuche misst,
 * dass etwas dasteht, nicht, dass es stimmt.** Ob eine getippte Zahl von der
 * gerechneten abweicht, muss man ausrechnen. Vierzehnte Datei dieser Art nach
 * `namen.ts`, `terminraster.ts`, `klebeband.ts`, `kartenmass.ts`,
 * `oberflaeche.ts`, `kartenebene.ts`, `grenzpunkte.ts`, `handbuchmarken.ts`,
 * `tabellenbeschriftung.ts`, `zahlwoerter.ts`, `bruchstellen.ts`,
 * `anlasstext.ts`, `auftragsuche.ts` und `technikfelder.ts`.
 */

/** Eine Zeile, so wie sie der Server liefert (nur, was hier gebraucht wird). */
export interface Zeile {
  aufmassPositionId?: string;
  oz: string;
  /**
   * DIE LEISTUNGSBEZEICHNUNG AUS DEM RAHMENVERTRAG.
   *
   * ORIGINALTEXT MARVIN (14.09.2026, Bild 6)
   *   „hier fehlen die Leistungsbezeichnungen"
   *
   * Sie kam die ganze Zeit vom Server mit (`Eintrag.text` in
   * `api/src/regeln/leb.ts`) und stand in dieser Aufstellung nicht: übrig
   * blieb die Ordnungszahl. **„10030364" ist eine Zahl, „Zulage
   * Hausanschluss >1 m" ist eine Leistung** - und wer eine Menge ändert,
   * bevor sie hinausgeht, ändert eine Leistung und keine Nummer. Derselbe
   * Satz steht seit dem 10.09.2026 über der Aufstellung auf dem Bildschirm
   * darunter; **ein Fenster, das dieselben Zeilen zeigt und dieselbe Angabe
   * weglässt, ist die Stelle, an der jemand die falsche Zahl ändert.**
   *
   * GERECHNET WIRD MIT IHR NICHT, und deshalb darf sie leer sein: eine
   * Position, die der Leistungskatalog nicht kennt, trägt keinen Text. Der
   * Bildschirm sagt das hin, statt eine leere Zelle zu zeigen.
   */
  text: string;
  menge: number;
  mengeGerechnet: number;
  grund: string | null;
  geaendert: boolean;
  /** Der Vermerk, der WIRKLICH in die Datei geht (`sectionText`). */
  sectionText: string;
  /** Was das System als Vermerk gerechnet hat: der Name des Abschnitts. */
  sectionTextGerechnet: string;
  /** Hat ein Mensch den Vermerk für diesen Export von Hand gesetzt? */
  vermerkGeaendert: boolean;
}

/** Was in den 3 Feldern einer Zeile steht. Immer Text: es sind Eingabefelder. */
export interface Eingabe {
  menge: string;
  grund: string;
  vermerk: string;
}

/**
 * Eine getippte Menge.
 *
 * Dieselbe Deutung wie `alsMenge` im Server: der Punkt ist ein
 * Tausendertrenner, das Komma trennt die Nachkommastellen. Was keine Zahl
 * ist, gibt `null` und wird nicht geraten - eine geratene 0 hiesse „diese
 * Position geht nicht mit", und das hat niemand gesagt.
 */
export function mengeAusFeld(w: string): number | null {
  const s = String(w ?? '').trim();
  if (!s) return null;
  const n = Number(s.replace(/\./g, '').replace(',', '.'));
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 1000) / 1000;
}

/** Wie eine Menge im Eingabefeld steht. Dezimalkomma, nachlaufende Nullen weg. */
export function feldAusMenge(n: number): string {
  const s = (Math.round((Number(n) || 0) * 1000) / 1000)
    .toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
  return (s === '' || s === '-' ? '0' : s).replace('.', ',');
}

/**
 * DIE HÖCHSTLÄNGE DES VERMERKS STEHT NICHT MEHR HIER.
 *
 * ORIGINALTEXT MARVIN (15.09.2026, Bild 11)
 *   „in der vergabe sind bei dem vermerk, also abschnitt nur 40 Zeihen
 *    erlaubt, trage das auch bei den tiefbauern in den feldern ein als
 *    maximale eingabe."
 *
 * **HIER STAND EINE 200, UND SIE WAR UNSERE EIGENE.** Sie stand mit Absicht
 * als unsere da, weil die Schreibvorschrift des Portals nicht vorlag. Jetzt
 * liegt sie vor, und sie ist 40.
 *
 * Sie kommt ab jetzt mit der Antwort des Servers (`vermerkHoechstens`) und
 * wird durchgereicht. **Es gibt keinen Vorgabewert.** Ein Vorgabewert wäre
 * genau die 2. Fassung, die hier gerade weggefallen ist: sie sähe richtig aus,
 * solange der Server dieselbe Zahl schickt, und wäre an dem Tag falsch, an dem
 * er eine andere schickt - ohne dass sich etwas meldet. Fehlt die Angabe,
 * prüft das Fenster nicht auf Länge und sagt das hin; der Server weist die
 * Zeile dann ab, und das ist der richtige Ort dafür.
 */
export type Hoechstlaenge = number | null;

/** Der Stand der Felder, wie er beim Öffnen dasteht. */
export function eingabenAus(zeilen: Zeile[]): Record<string, Eingabe> {
  const raus: Record<string, Eingabe> = {};
  for (const z of zeilen) {
    if (!z.aufmassPositionId) continue;
    raus[z.aufmassPositionId] = {
      menge: feldAusMenge(z.menge),
      grund: z.grund ?? '',
      vermerk: z.sectionText ?? '',
    };
  }
  return raus;
}

/** Eine Zeile, so wie sie an den Server geht. */
export interface Aenderung {
  aufmass_position_id: string;
  /** `null`, wo nur der Vermerk gesetzt ist. */
  menge: number | null;
  /** Warum die Menge abweicht. `null`, wo keine Menge gesetzt ist. */
  grund: string | null;
  /** `null`, wo der gerechnete Vermerk gilt. */
  vermerk: string | null;
}

export interface Vorhaben {
  /** Was an den Server geht. Die Liste ist VOLLSTÄNDIG. */
  aenderungen: Aenderung[];
  /** Zeilen, deren Menge sich nicht lesen lässt. */
  unlesbar: string[];
  /** Zeilen, die abweichen und keinen Grund tragen. */
  ohneGrund: string[];
  /** Zeilen, deren Vermerk länger ist, als er sein darf. */
  zuLang: string[];
}

/**
 * Was gespeichert wird.
 *
 * EINE ZEILE, DIE AUF DER GERECHNETEN MENGE STEHT, IST KEINE ÄNDERUNG. Wer
 * sie dorthin zurücksetzt, NIMMT seine Änderung zurück, statt eine anzulegen,
 * die genau das sagt, was ohnehin gilt - dieselbe Regel wie im Rechteraster,
 * wo ein Haken auf dem Stand seiner Gruppe die Ausnahme löscht. Sonst stünde
 * nach 3 Klicks eine Zeile da, die niemand mehr einordnen kann. Für den
 * Vermerk gilt dasselbe: steht er auf dem Abschnittsnamen, ist er gerechnet.
 *
 * DER GRUND IST PFLICHT, SOBALD DIE MENGE ABWEICHT - und nur dann. Beim
 * Vermerk ist er es NICHT: er GEHT IN DIE DATEI und trägt seine Begründung
 * damit selbst, während die Menge nur als Zahl hinausgeht.
 *
 * UND EINE ZEILE, DIE NUR DEN VERMERK MEINT, TRÄGT KEINE MENGE. Wer dort die
 * gerechnete einträgt, hat morgen eine Änderung, die eine Menge festschreibt,
 * die inzwischen eine andere sein könnte - und niemand wüsste, dass das nie
 * jemand entschieden hat.
 */
export function vorhabenAus(
  zeilen: Zeile[], eingaben: Record<string, Eingabe>,
  /**
   * Die Höchstlänge des Vermerks, wie der Server sie nennt. `null` heisst:
   * der Server hat nichts dazu gesagt, also wird hier nicht auf Länge
   * geprüft - **eine geratene Grenze weist eine Eingabe ab, die angekommen
   * wäre.**
   */
  hoechstens: Hoechstlaenge = null
): Vorhaben {
  const aenderungen: Aenderung[] = [];
  const unlesbar: string[] = [];
  const ohneGrund: string[] = [];
  const zuLang: string[] = [];

  for (const z of zeilen) {
    const id = z.aufmassPositionId;
    if (!id) continue;
    const e = eingaben[id];
    if (!e) continue;

    const getippt = mengeAusFeld(e.menge);
    if (getippt === null) { unlesbar.push(z.oz); continue; }

    const vermerkRoh = (e.vermerk ?? '').trim();
    if (hoechstens !== null && vermerkRoh.length > hoechstens) {
      zuLang.push(z.oz); continue;
    }
    const vermerk = vermerkRoh === (z.sectionTextGerechnet ?? '').trim()
      ? null : vermerkRoh;

    const menge = getippt === z.mengeGerechnet ? null : getippt;
    if (menge === null && vermerk === null) continue;

    let grund: string | null = null;
    if (menge !== null) {
      grund = (e.grund ?? '').trim();
      if (!grund) { ohneGrund.push(z.oz); continue; }
    }
    aenderungen.push({ aufmass_position_id: id, menge, grund, vermerk });
  }

  return { aenderungen, unlesbar, ohneGrund, zuLang };
}

/**
 * Die Liste, wie sie GERADE gespeichert ist.
 *
 * Sie steht in den Zeilen selbst: eine Zeile mit `geaendert` trägt in `menge`
 * die von Hand gesetzte Zahl und in `grund` den Satz dazu, eine mit
 * `vermerkGeaendert` in `sectionText` das gesetzte Wort. Eine zweite Quelle
 * daneben liefe beim ersten Nachtrag auseinander.
 */
export function bestandsliste(zeilen: Zeile[]): Aenderung[] {
  return zeilen
    .filter((z) => (z.geaendert || z.vermerkGeaendert) && z.aufmassPositionId)
    .map((z) => ({
      aufmass_position_id: z.aufmassPositionId as string,
      menge: z.geaendert ? z.menge : null,
      grund: z.geaendert ? (z.grund ?? '').trim() : null,
      vermerk: z.vermerkGeaendert ? (z.sectionText ?? '').trim() : null,
    }));
}

/**
 * Die VOLLSTÄNDIGE Liste, wenn EINE Zeile gesetzt wird.
 *
 * ORIGINALTEXT MARVIN (10.09.2026)
 *   „am ende jeder spalte machst du ein symbol um direkt dort live zu ändern
 *    und ein symbol um die spalte zu löschen, bzw. deaktiereven, so dass sie
 *    nicht in die csv kommt."
 *
 * **DER WEG NIMMT DIE GANZE LISTE ODER GAR NICHTS.** `PUT /leb/mengen` löscht
 * alle Änderungen dieses Auftrags und schreibt die übergebenen neu - wer nur
 * die eine Zeile schickte, an der er gerade steht, nähme jeder anderen
 * lautlos ihre Änderung weg. Ein Handgriff an EINER Zeile ist deshalb: den
 * Bestand nehmen, diese eine ersetzen, alles zurückschicken.
 *
 * **EIN WERT AUF DEM GERECHNETEN STAND IST KEINE ÄNDERUNG.** Wer ihn dorthin
 * zurückstellt, NIMMT seine Änderung zurück - dieselbe Regel wie im
 * Sammelfenster und im Rechteraster. Und wer BEIDE zurückstellt, hat gar
 * keine Änderung mehr: die Zeile fällt ganz weg.
 */
export function listeMitZeile(
  zeilen: Zeile[], id: string, menge: number, grund: string, vermerk: string
): Aenderung[] {
  const ohne = bestandsliste(zeilen)
    .filter((a) => a.aufmass_position_id !== id);
  const ziel = zeilen.find((z) => z.aufmassPositionId === id);
  if (!ziel) return ohne;

  const v = (vermerk ?? '').trim();
  const neuerVermerk = v === (ziel.sectionTextGerechnet ?? '').trim()
    ? null : v;
  const neueMenge = menge === ziel.mengeGerechnet ? null : menge;
  if (neueMenge === null && neuerVermerk === null) return ohne;

  return [...ohne, {
    aufmass_position_id: id,
    menge: neueMenge,
    grund: neueMenge === null ? null : (grund ?? '').trim(),
    vermerk: neuerVermerk,
  }];
}

/** Dieselbe Liste ohne die Änderung an dieser Zeile: sie gilt wieder gerechnet. */
export function listeOhneZeile(zeilen: Zeile[], id: string): Aenderung[] {
  return bestandsliste(zeilen).filter((a) => a.aufmass_position_id !== id);
}

/**
 * Hat sich gegenüber dem Stand etwas geändert?
 *
 * Verglichen wird der INHALT und nicht die Adresse des Objekts: die Eingaben
 * werden bei jedem Zeichnen neu gebaut, und mit `===` stünde „noch nicht
 * gespeichert" dauerhaft da.
 */
export function nochNichtGespeichert(
  zeilen: Zeile[], eingaben: Record<string, Eingabe>
): boolean {
  const jetzt = vorhabenAus(zeilen, eingaben).aenderungen;
  const vorher = bestandsliste(zeilen);
  const schluessel = (l: Aenderung[]) => l
    .map((a) => `${a.aufmass_position_id}|${a.menge}|${a.grund}|${a.vermerk}`)
    .sort().join('\n');
  return schluessel(jetzt) !== schluessel(vorher);
}
